import { z } from "zod";
/**
 * The body-photo assessment on the wire — consent, the two photos, the number
 * that comes back, and the personal anchor (`D16`–`D26` of the body-photos
 * plan, which amend `D99`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ── WHAT THE CHAIN IS, SO THE SHAPES BELOW READ AS ONE THING ───────────────
 * Two full-body photos — front and side, head to feet — plus height, weight,
 * age and sex go to `bodycomp`, a stateless service on the VPS. It answers a
 * **waist-to-height ratio** (the primary number) and a **body-fat percentage
 * with a 95% range**, both from the deterministic RFM equation. Its LLM never
 * produces a number: it checks the photo and may only WIDEN the body-fat range
 * and set `comparable: false` (`D16`, `D17`).
 *
 * So everything here is the app's half of a service this repository does not
 * own. The numeric bounds are RESTATED from `bodycomp`'s own request model, not
 * imported — nothing in this workspace can import it — and a bound LOOSER than
 * the service's turns its 422 into a 500 nobody can render, while a bound
 * tighter refuses a photo it would have accepted. Equal is the only right
 * answer, which is why each constant below names the file it was read from.
 *
 * ── THE PHOTOS ARE NOT IN ANY OF THESE SHAPES, AND THAT IS THE POINT ───────
 * They travel as `multipart/form-data` parts on the create, are kept in the
 * private R2 bucket (`D20`), and are read back one at a time through their own
 * route. `bodyAssessment.photos` says only whether each one still exists:
 * revoking consent deletes the photos and KEEPS the numbers, because the
 * numbers are the trend (`D20`, `D21`).
 *
 * ── THE ROUTES ARE PINNED HERE AND MOUNTED ELSEWHERE ───────────────────────
 * `TRAINING_ROUTES` (`routes.ts`) is probed end to end by
 * `apps/api/src/__tests__/pool/route-reachability.test.ts`, which fails for any
 * declared path that is not mounted. Declaring them here and mounting them a
 * task later would leave `@repo/api` red in between, so the rows are added by
 * the task that mounts them. What they will be:
 *
 * | key                    | path                                   | verbs |
 * |---|---|---|
 * | `bodyPhotoConsent`     | `/me/body-photo-consent`               | `GET`, `POST` (201 new / 200 same version already active), `DELETE` (204 — revokes and deletes every photo) |
 * | `bodyAssessments`      | `/me/body-assessments`                 | `GET` list, `POST` create (201 created / 200 replay) |
 * | `bodyAssessment`       | `/me/body-assessments/:id`             | `GET` (read-through poll while pending), `DELETE` (204 — soft-deletes, deletes photos) |
 * | `bodyAssessmentPhoto`  | `/me/body-assessments/:id/photos/:view`| `GET` `image/jpeg`, `Cache-Control: private, no-store`; 404 once deleted |
 * | `bodyAnchor`           | `/me/body-anchor`                      | `GET`, `DELETE` (204) |
 * | `bodycompWebhook`      | `/webhooks/bodycomp`                   | `POST`, no auth, HMAC-verified |
 *
 * ── INPUTS ARE STRICT, OUTPUTS ARE NOT ────────────────────────────────────
 * The same rule as every other file here (`measurement.ts`): `z.strictObject`
 * on the way in so a misspelt key is a 422 the outbox quarantines rather than
 * an answer silently dropped, `z.object` on the way out so a response the
 * server later widens still parses in an installed APK (`D163`, `D172`).
 *
 * The usual second half of that rule — *a new output field is optional, because
 * an older server does not send it* — does NOT apply to the nullables below,
 * and deliberately. This whole family is new: the oldest server that serves any
 * of these routes is the one that introduces them, so there is no deployed API
 * that answers a `bodyAssessment` without `warnings` or without `photos`. A
 * field added AFTER this ships is a different matter and takes `.optional()`.
 * ═══════════════════════════════════════════════════════════════════════════
 */
/**
 * The version of the consent text the app is showing. Stored with the grant,
 * so a change to what the person agreed to is a change they are asked about
 * again: `consent.current` is `false` for every account still holding an older
 * version, and the create route refuses with `body_assessment.consent_outdated`
 * (`D21`).
 *
 * A date and not a counter because the text's history is a document history.
 * **Bumping it makes everyone re-consent** — that is the whole mechanism, and
 * it is not a formality: photographs of a body and the numbers derived from
 * them are sensitive personal data under the LGPD, and `D17` puts a face-masked
 * copy in front of a model outside the country, which the text has to say.
 */
export const BODY_PHOTO_CONSENT_VERSION = "2026-09-19";
/**
 * `bodycomp` answers 422 `AGE_OUT_OF_RANGE` under 15 (`app/schemas/request.py`),
 * so the app hides the feature entirely below it with an explanation rather
 * than letting a teenager photograph themselves for an error (`D24`).
 *
 * The upper bound it also carries (120) is not restated: nothing is gained by
 * refusing on this side an age the server will refuse anyway, and the screen
 * has no different thing to say about it.
 */
export const BODY_ASSESSMENT_MIN_AGE = 15;
/**
 * Per photo, on the way in. `bodycomp` itself accepts 10 MB; this is a tenth of
 * that on purpose — the in-app camera writes a JPEG no wider than 1536 px
 * (`D22`), which lands around 300–600 kB, so 2 MB is generous for a phone that
 * encodes badly and still small enough that two of them do not threaten the
 * Worker's 128 MB isolate.
 *
 * Over it the route answers 413 `body_assessment.photo_too_large` rather than
 * streaming megabytes to the VPS to be refused there.
 */
export const BODY_ASSESSMENT_PHOTO_MAX_BYTES = 2 * 1024 * 1024;
/**
 * The shutter is blocked outside ±5° of vertical and the pipeline refuses the
 * same angle with 422 `DEVICE_TILTED` (`gate_device_max_pitch_deg`), because
 * pitch distorts the height-in-pixels that is half the ratio.
 *
 * Two gates on one number, and this constant is what keeps them equal: the
 * camera's live level (`D22`) and the create input's bound are the same 5.
 */
export const BODY_ASSESSMENT_MAX_PITCH_DEG = 5;
/**
 * The two photos, named. This tuple is the order they are asked for, the names
 * of the multipart parts, and the `:view` segment of the photo route — one
 * spelling for all three, because a part named `frontal` on one side and
 * `front` on the other is a 400 with nothing in the logs to say which half was
 * wrong.
 */
export const BODY_ASSESSMENT_PHOTO_VIEWS = ["front", "side"];
export const bodyAssessmentPhotoView = z.enum(BODY_ASSESSMENT_PHOTO_VIEWS);
/**
 * How a same-day reference reading was taken, best first
 * (`app/schemas/request.py`). The method decides how much the anchor is
 * trusted: `bia_gym` and `bia_scale` need at least three readings before the
 * service accepts them at all, which is why `reference.readings` exists.
 *
 * Ordered by quality and not alphabetically, because the picker shows them in
 * this order and a reordering here would silently reorder that screen.
 */
export const BODY_FAT_REFERENCE_METHODS = [
    "dxa_same_machine",
    "dxa",
    "adp",
    "bia_research",
    "bia_gym",
    "bia_scale",
];
export const bodyFatReferenceMethod = z.enum(BODY_FAT_REFERENCE_METHODS);
/**
 * Every warning code `bodycomp` publishes today (`app/i18n/catalog.py`).
 *
 * They are snake_case en-US and never translated: the service returns the CODE
 * and this app writes the sentence, which is the only arrangement under which
 * one deployment can serve a pt-BR and an en-US client. `visceral_fat` is
 * always present and always last — the pipeline cannot see visceral fat and
 * says so on every result.
 *
 * Three of the eleven (`arms_close`, `trunk_tilted`, `device_tilted`) are
 * catalogued and never emitted. They stay listed because they are published
 * codes and the service may start emitting them without asking.
 */
export const BODYCOMP_WARNING_CODES = [
    "visceral_fat",
    "diagnostic_spread",
    "arms_close",
    "trunk_tilted",
    "device_tilted",
    "scale_mismatch",
    "waist_fallback",
    "whtr_low",
    "tail_extreme",
    "perspective_uncorrected",
    "llm_not_comparable",
];
/**
 * The four photo-protocol checks the LLM runs, in the order `bodycomp` reports
 * them (its `WIDEN_PP` order, `app/llm/verdict.py`) — the values that arrive in
 * `bodyAssessmentWarning.fields` when `llm_not_comparable` fires.
 *
 * Listed here so the screen that renders *"loose clothing, shoes on"* has a
 * source for those four words instead of four string literals nobody can trace
 * back to the service that produces them.
 */
export const BODYCOMP_LLM_PROTOCOL_FIELDS = [
    "clothing_tight",
    "hair_clear",
    "barefoot",
    "arms_clear",
];
/**
 * The sex the RFM equation uses (`+12` for female). Two values and not
 * `ATHLETE_SEXES`: the profile's third answer is *"prefer not to say"*
 * (`profile.ts`), and the equation has no term for it — so where the profile
 * says `other` the assessment ASKS, and stores the answer on the assessment
 * alone, never back onto the profile (`D24`).
 */
export const BODY_CALCULATION_SEXES = ["female", "male"];
export const bodyCalculationSex = z.enum(BODY_CALCULATION_SEXES);
/** NICE's waist-to-height bands, the primary number's verdict. */
export const BODY_WHTR_BANDS = ["low", "healthy", "increased", "high"];
export const bodyWhtrBand = z.enum(BODY_WHTR_BANDS);
/**
 * Where the body-fat range came from. `calibrated` is the population interval
 * (held-out NHANES, ~12–15 pp wide); `anchor` is the narrower one a personal
 * reference reading earns. The app says which, because a range that silently
 * halves is a result the person will read as a better measurement.
 */
export const BODY_FAT_RANGE_MODELS = ["calibrated", "anchor"];
export const bodyFatRangeModel = z.enum(BODY_FAT_RANGE_MODELS);
// ── consent ────────────────────────────────────────────────────────────────
/**
 * `POST /me/body-photo-consent` — the person agreed to the text they were
 * shown, and the app says WHICH text (`D21`).
 *
 * `bodycomp` accepts a `consent` field and stores nothing with it, so the
 * record is ours or it does not exist. The version travels rather than being
 * assumed server-side, because the app the person is holding may be older than
 * the Worker: a server that stamped its own
 * {@link BODY_PHOTO_CONSENT_VERSION} would record agreement to a paragraph
 * nobody ever read.
 *
 * 201 for a new grant, 200 when the same version is already active — a
 * double-tap is not a second consent.
 */
export const bodyPhotoConsentInput = z.strictObject({
    textVersion: z.string().min(1).max(32),
});
/**
 * `GET /me/body-photo-consent`, and the answer to `POST`.
 *
 * `null` means never granted OR revoked, and the screen treats them the same:
 * the offer to take photos, with the text. Revocation is not a tombstone the
 * app has to reason about — it deletes the photos and leaves the numbers
 * (`D20`), and what is left to say about it is nothing.
 */
export const bodyPhotoConsentOutput = z.object({
    consent: z
        .object({
        /**
         * `true` exactly when `textVersion === BODY_PHOTO_CONSENT_VERSION`.
         *
         * Computed by the SERVER and not by the app comparing the two strings,
         * because the version the create route enforces is the server's. An app
         * older than a bump would otherwise show a green tick and then be
         * refused with `body_assessment.consent_outdated` at the shutter.
         */
        current: z.boolean(),
        grantedAt: z.iso.datetime(),
        textVersion: z.string(),
    })
        .nullable(),
});
// ── create ─────────────────────────────────────────────────────────────────
/**
 * `POST /me/body-assessments` — `multipart/form-data` with exactly three parts:
 *
 * - `meta`  — the JSON text of THIS schema;
 * - `front` — `image/jpeg`, at most {@link BODY_ASSESSMENT_PHOTO_MAX_BYTES};
 * - `side`  — the same.
 *
 * A JSON part rather than a flat form because the shape is nested and typed:
 * every value in a `multipart` field arrives as a string, so a flat form would
 * make every number below a hand-written `Number()` on the server, and
 * `reference` would have to be spelt out as three unrelated fields with no way
 * to require them together.
 *
 * 201 on a create, 200 on a replay — and the replay is the whole reason `id` is
 * the client's (see below).
 */
export const bodyAssessmentCreateMeta = z.strictObject({
    /**
     * The 35 mm-equivalent focal length, when the browser will say
     * (`camera_focal_35mm`, 10–300). With it the pipeline corrects keystone
     * distortion; without it, it does not, and the result carries
     * `perspective_uncorrected`.
     *
     * Optional and expected to be ABSENT on the phone: no web API exposes it, and
     * `D26` accepts that warning in v1 rather than buying it with another native
     * build. It stays in the shape because a future native camera plugin would
     * fill it in and nothing else would have to change.
     */
    cameraFocal35mm: z.number().min(10).max(300).optional(),
    /**
     * Read from `DeviceOrientationEvent` at the moment of the shutter (`D22`).
     * Required, as it is on `bodycomp`: absent, the pipeline cannot tell an
     * upright phone from a tilted one, and the tilt is what bends the height in
     * pixels that the whole ratio rests on.
     *
     * Bounded to ±{@link BODY_ASSESSMENT_MAX_PITCH_DEG} here although the service
     * accepts ±90 and then refuses beyond 5 — the app blocks the shutter at the
     * same angle, so a value outside it is a bug in the camera screen and should
     * be a 422 from our own route rather than a round trip to the VPS.
     */
    devicePitchDeg: z
        .number()
        .min(-BODY_ASSESSMENT_MAX_PITCH_DEG)
        .max(BODY_ASSESSMENT_MAX_PITCH_DEG),
    heightCm: z.number().min(100).max(250),
    /**
     * A client-minted UUIDv7 (`D163`), and REQUIRED where every other replayable
     * write here takes it as optional.
     *
     * The difference is what a duplicate costs. A resent set is a row; a resent
     * assessment is two uploads of two photos, two jobs on a queue that allows
     * twenty a minute for everybody (`D26`), and two rows in a trend the person
     * reads as their body changing. So there is no un-keyed form of this call to
     * fall back to: a repeat of the same `id` answers the stored assessment with
     * `created: false` and starts nothing.
     *
     * Mint it with the `uuidv7` helper, never `crypto.randomUUID()` — that does
     * not exist on the live-reload origin (`T-174`).
     */
    id: z.uuid(),
    /**
     * A same-day DXA, ADP or bioimpedance reading, which sets the account's
     * personal anchor and narrows every later range (`D23`).
     *
     * Sent only when there IS one, and only once — with no `reference` the server
     * applies the stored anchor if the account has one. Sending it again with a
     * new reading replaces the anchor; `bodycomp` refuses the two together, and
     * so does the route.
     */
    reference: z
        .strictObject({
        bodyFatPercent: z.number().min(2).max(75),
        method: bodyFatReferenceMethod,
        /**
         * How many readings the number is the mean of. `bia_gym` and `bia_scale`
         * need at least three before the service will take them at all (422
         * `ANCHOR_REJECTED` otherwise); a single DXA is one reading and may say
         * so, or say nothing, which means one.
         */
        readings: z.number().int().min(1).max(20).optional(),
    })
        .optional(),
    /**
     * REQUIRED when the profile's sex is `other`, ignored otherwise (`D24`).
     *
     * Optional in the schema and conditional in the route, because the condition
     * is a fact about the account the contract cannot see. Missing where it is
     * needed answers 422 `body_assessment.sex_required` — a named refusal the
     * screen can turn into the question, rather than a `request.invalid_body` it
     * can only apologise for.
     */
    sexForCalculation: bodyCalculationSex.optional(),
    /**
     * A tape measure round the navel, in centimetres, when the person has one
     * (`D23`). It replaces the waist the pipeline measured in pixels and shifts
     * the method to `rfm+tape` — the single biggest accuracy gain available
     * without a DXA.
     *
     * Optional: the whole point of the feature is a number from two photos and
     * nothing else.
     */
    waistTapeCm: z.number().min(30).max(250).optional(),
    weightKg: z.number().min(20).max(400),
});
// ── the result ─────────────────────────────────────────────────────────────
/**
 * One warning, as a code plus its parameters — never as a sentence.
 *
 * `bodycomp` used to return pt-BR display text and nothing else; it now returns
 * `warning_details` alongside, index-aligned with the text and de-duplicated
 * the same way, and THIS is the half the app reads. The sentence is written
 * here, in the reader's language.
 *
 * `code` is a bare `z.string()` and not `z.enum(BODYCOMP_WARNING_CODES)`, which
 * is the one deliberate looseness in this file. The list belongs to a service
 * deployed on its own clock: a code added there would make an enum refuse the
 * WHOLE assessment — no ratio, no percentage, no photos — over a sentence that
 * could not be written. An unknown code renders generically instead, and
 * {@link BODYCOMP_WARNING_CODES} is what the renderer switches on.
 */
export const bodyAssessmentWarning = z.object({
    /** One of {@link BODYCOMP_WARNING_CODES} today. See above. */
    code: z.string(),
    /**
     * The disagreement between the diagnostic equations, in percentage points,
     * rounded to two decimals. Only `diagnostic_spread` carries it; `null`
     * everywhere else, which is not the same as zero.
     */
    spreadPp: z.number().nullable(),
    /**
     * Which of {@link BODYCOMP_LLM_PROTOCOL_FIELDS} failed, in that order. Only
     * `llm_not_comparable` carries it. `null` — not `[]` — elsewhere, so that
     * "does not apply" stays distinguishable from "applies and is empty".
     */
    fields: z.array(z.string()).nullable(),
});
/**
 * `pending` until the job settles, then `done` or `failed`.
 *
 * `pending` is the normal first answer and not an error: the service is async
 * by default and takes two to six seconds, the webhook has never once been
 * observed to arrive, and the poll is the safety net that makes the difference
 * invisible.
 */
export const BODY_ASSESSMENT_STATUSES = ["pending", "done", "failed"];
export const bodyAssessmentStatus = z.enum(BODY_ASSESSMENT_STATUSES);
/**
 * The POSE_INVALID reasons `bodycomp` names today (`D241`). A list for the
 * catalogue test and the capture screen's own pose check, which reports in
 * the same words; NOT an enum on the wire — {@link bodyAssessmentRefusal}'s
 * `reason` stays a bare string, because the service adds checks on its own
 * release clock and an unknown reason renders as the code's own sentence.
 */
export const BODYCOMP_POSE_REASONS = [
    "no_person",
    "multiple_people",
    "body_not_visible",
    "not_standing",
    "feet_not_on_ground",
];
/**
 * The gate codes that judge the person's pose and the phone, in the order the
 * service checks them — its order is the order the screen reports in
 * (`D241`). `POSE_INVALID` first, then the view gates and the device's pitch.
 */
export const BODYCOMP_GATE_CODES = [
    "POSE_INVALID",
    "FRONT_NOT_FRONTAL",
    "ARMS_TOO_CLOSE",
    "TRUNK_NOT_VERTICAL",
    "SIDE_NOT_PROFILE",
    "DEVICE_TILTED",
];
/**
 * What the service said beside a failed assessment's `errorCode` (`D241`):
 * the check that failed, the photo to RETAKE, and a geometric gate's
 * measured value against its limit.
 *
 * `view` is the one a screen acts on: the other photo is kept and only the
 * named one is taken again (both are still uploaded — the service is
 * stateless). `null` means "retake both", which is also what an assessment
 * that failed before this field existed reads as.
 */
export const bodyAssessmentRefusal = z.object({
    /** The gate's threshold, in the units of `value`. */
    limit: z.number().nullable(),
    /** For `POSE_INVALID`, one of {@link BODYCOMP_POSE_REASONS} today. */
    reason: z.string().nullable(),
    /** A degree, an elbow clearance in % of body height, a shoulder ratio. */
    value: z.number().nullable(),
    view: bodyAssessmentPhotoView.nullable(),
});
/**
 * One assessment, in every status. The same shape answers the create, the read,
 * the poll and each row of the list — one shape and not three, because a screen
 * that polls a pending assessment into a done one must not have to swap
 * schemas halfway.
 *
 * Which is why nearly everything a result carries is `nullable` rather than
 * optional: the fields are always PRESENT, and `null` says *"not yet, or never"*.
 * The alternative — omitting them while pending — would make `undefined` mean
 * both "still working" and "this server is older than the field".
 */
export const bodyAssessment = z.object({
    /** The person's age at the assessment, as the equation used it. Stored and
     *  echoed rather than recomputed from the date of birth, so a result read a
     *  year later still says what it was computed from. */
    ageYears: z.number().int(),
    /**
     * The body-fat percentage with its 95% range. `null` while pending and on a
     * failure.
     *
     * **Never shown without the range.** The measurement budget is about 3.6 pp
     * for men and 4.2 for women and the calibrated interval is 12–15 pp wide; a
     * bare percentage reads as a measurement it is not.
     */
    bodyFat: z
        .object({
        high: z.number(),
        low: z.number(),
        /** `rfm`, `rfmp`, `rfm+tape`, `rfm+anchor`, … — a bare string for the
         *  same reason `warning.code` is: the service composes these labels and
         *  may compose a new one. */
        method: z.string(),
        percent: z.number(),
        rangeModel: bodyFatRangeModel,
    })
        .nullable(),
    /**
     * `false` means the photo protocol failed — loose clothing, shoes, hair over
     * the outline, arms against the trunk — and the number cannot be compared
     * with the others. **Keep it OFF every trend**; show it alone, with what to
     * do differently.
     *
     * `null` until `done`, which is not `false`: an assessment still running has
     * not been judged incomparable, it has not been judged.
     */
    comparable: z.boolean().nullable(),
    completedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    /**
     * `bodycomp`'s own error code (`POSE_INVALID`, `ARMS_TOO_CLOSE`,
     * `SEGMENTATION_FAILED`, …) or `EXPIRED` when the job outlived its result
     * TTL with nothing collected. `null` unless `failed`.
     *
     * A code and not a message: a failed async job answers HTTP 200 with a code
     * and NO text at all, so the sentence is this app's to write in either case,
     * and writing it from a code keeps the two paths identical.
     */
    errorCode: z.string().nullable(),
    /**
     * The refusal's detail (`D241`), sent only on a `failed` row whose refusal
     * carried one. OPTIONAL rather than nullable, unlike its neighbours: a newer
     * field an older server never sends, so an app reading it must survive its
     * absence (`D172`), and absent reads exactly as "retake both".
     */
    errorDetail: bodyAssessmentRefusal.optional(),
    heightCm: z.number(),
    id: z.string(),
    /**
     * The LLM's photo notes, in the account's language — *"the shorts are loose
     * around the waist"*. Display-only, at most five, capped at 200 characters
     * each by the service, and **never read as a number**: this model does not
     * produce one.
     *
     * `[]` while pending, and `[]` is also a legitimate final answer.
     */
    notes: z.array(z.string()),
    /**
     * Whether each photo is still in the bucket. `false` once deleted — which
     * happens on revocation, on deleting the assessment, and on account deletion
     * (`D20`) — and the row keeps its numbers either way.
     */
    photos: z.object({ front: z.boolean(), side: z.boolean() }),
    /**
     * The exact pipeline that produced these numbers
     * (`cv-3.1+rfm-1.0+k-1.0153+iv-nhanes-…`, and it differs by sex). `null`
     * while pending.
     *
     * Stored because a new version changes the number without the body changing:
     * the history is replayed through `/v1/recompute` when it moves, and until
     * then a row computed under an older version is not comparable with a newer
     * one (`D23`).
     */
    pipelineVersion: z.string().nullable(),
    /** When this row was last replayed through `/v1/recompute`; `null` if never. */
    recomputedAt: z.iso.datetime().nullable(),
    /** Which sex term the equation used — the profile's, or the one the person
     *  gave for the calculation where the profile says `other` (`D24`). */
    sexForCalculation: bodyCalculationSex,
    status: bodyAssessmentStatus,
    /** The tape waist that was sent, if one was; `null` otherwise. It is on the
     *  row and not only on the request because it changes the method, and a
     *  trend that mixes taped and untaped rows has to be able to say so. */
    waistTapeCm: z.number().nullable(),
    weightKg: z.number(),
    /**
     * The primary number: waist ÷ height, with the NICE band. **Lead with this**,
     * not with the body fat — it is the better-evidenced measure and the range on
     * it is ±0.018 rather than ±6 pp.
     *
     * `nearThreshold` is true within 0.02 of a band edge, and when it is, the
     * screen says the person is on a boundary rather than announcing the band as
     * a verdict.
     */
    whtr: z
        .object({
        band: bodyWhtrBand,
        high: z.number(),
        low: z.number(),
        nearThreshold: z.boolean(),
        value: z.number(),
    })
        .nullable(),
    /** `[]` until `done`. Codes, not sentences — see {@link bodyAssessmentWarning}. */
    warnings: z.array(bodyAssessmentWarning),
});
/**
 * The answer to `POST /me/body-assessments`.
 *
 * `created` is what the status code already says (201 against 200) and it is
 * here anyway, because the offline outbox does not always see the status code —
 * a replay after a timeout is exactly the case where it did not. A client that
 * reads `created: false` knows its photos were already accepted and does not
 * offer to take them again.
 */
export const bodyAssessmentCreated = z.object({
    assessment: bodyAssessment,
    created: z.boolean(),
});
/**
 * `GET /me/body-assessments` — newest first, soft-deleted rows excluded, at
 * most 200.
 *
 * Unpaged, deliberately: the ceiling is above any plausible history of a
 * measurement taken every few weeks, and a cursor on a list that short is
 * machinery with nothing to do. Whole rows and not a summary, because the trend
 * chart, the freshness check on the profile's completeness ring and the "your
 * last photos" card all read the same list.
 */
export const bodyAssessmentListOutput = z.object({
    items: z.array(bodyAssessment),
});
/**
 * `GET /me/body-anchor` — the account's personal calibration (`D23`), or `null`
 * when there is none.
 *
 * It is an OFFSET in percentage points and not the reference reading itself:
 * the DXA number was true on the day it was taken, and what survives it is the
 * distance between that machine and this equation for this body. `sdAfterPp` is
 * the residual spread once the offset is applied — the thing that actually
 * narrows the range, and the reason a `bia_scale` anchor helps less than a DXA
 * one.
 *
 * `DELETE` drops it; later assessments go back to the calibrated range.
 */
export const bodyAnchorOutput = z.object({
    anchor: z
        .object({
        method: bodyFatReferenceMethod,
        offsetPp: z.number(),
        readings: z.number().int(),
        sdAfterPp: z.number(),
        setAt: z.iso.datetime(),
        /** The assessment whose `reference` set it — so the screen can say
         *  *"from your DXA on 3 September"* and link to that row. */
        sourceAssessmentId: z.string(),
    })
        .nullable(),
});
/**
 * Every `messageKey` this family's errors carry, in the `server-body-assessment`
 * namespace.
 *
 * A tuple and not a loose set of strings so the test that holds the catalogue
 * complete can iterate it: a key added here with no message compiles, deploys,
 * and renders as `error.unrecognized` to the person — the façade in
 * `@repo/client/i18n` turns an unknown key into that rather than printing it,
 * so the failure is silent and looks like a generic server error (`T-164`).
 *
 * The status each one rides on:
 *
 * | key | status | when |
 * |---|---|---|
 * | `body_assessment.consent_required`   | 409 | no consent on record |
 * | `body_assessment.consent_outdated`   | 409 | consent older than {@link BODY_PHOTO_CONSENT_VERSION} |
 * | `body_assessment.age_out_of_range`   | 422 | under {@link BODY_ASSESSMENT_MIN_AGE}, or no date of birth |
 * | `body_assessment.sex_required`       | 422 | profile sex is `other` and `sexForCalculation` was omitted |
 * | `body_assessment.photo_missing`      | 400 | a multipart part is absent |
 * | `body_assessment.photo_too_large`    | 413 | over {@link BODY_ASSESSMENT_PHOTO_MAX_BYTES} |
 * | `body_assessment.photo_unsupported`  | 415 | not a JPEG |
 * | `body_assessment.not_found`          | 404 | unknown id, another account's, or deleted |
 * | `body_assessment.too_many_requests`  | 429 | the tenant's 20/min, shared by everyone (`D26`) |
 * | `body_assessment.unavailable`        | 503 | `bodycomp` down, queue full, or models not ready |
 */
export const BODY_ASSESSMENT_MESSAGE_KEYS = [
    "body_assessment.consent_required",
    "body_assessment.consent_outdated",
    "body_assessment.age_out_of_range",
    "body_assessment.sex_required",
    "body_assessment.photo_missing",
    "body_assessment.photo_too_large",
    "body_assessment.photo_unsupported",
    "body_assessment.not_found",
    "body_assessment.too_many_requests",
    "body_assessment.unavailable",
];
//# sourceMappingURL=body-assessment.js.map